import React, { useMemo } from 'react'
import moment from 'moment'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import config from 'constants/config'
import { take } from 'lib/arrayUtils'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import { connect } from 'react-redux'
import { getCustomerType } from 'lib/peopleLikeMeUtils'
import useHotelOfferSales from 'hooks/useHotelOfferSales'
import PeopleLikeMeSalesBanner from '../../../../SearchV2/Components/PeopleLikeMe/PeopleLikeMeSalesBanner'
import ViewBundleOffer from '.././ViewBundleOffer'
import SearchTilePropertyDetails from './SearchTilePropertyDetails'
import SearchTilePriceDetails from './SearchTilePriceDetails'
import { Main, MainImageSegment, OfferOverlayContainer, StyledOfferBadge, DetailSegment, StyledCSSBreakpoint, ViewBundleOfferWrapper } from './SearchTileStyles'

interface MappedProps {
  iOS: boolean;
  shouldShowSaleNumbers: boolean;
  windowSearch: string;
}

interface Props {
  offer: App.Offer | App.OfferSummary;
  offerMetaData?: App.OfferListMetaData;
  bestPriceForDates: App.OfferAvailableRate;
  fetchingPriceForDates: boolean
  soldOut: boolean
  filters?: App.OfferListFilters;
  bestPricePackage: App.Package;
  eagerLoadFirstImage?: boolean;
  offerUrl: string;
  offerLinkIncludesFilters?: boolean;
}

function NewSearchFlashOfferExpanded(props: Props & MappedProps) {
  const {
    offer,
    bestPriceForDates,
    soldOut,
    filters,
    bestPricePackage,
    eagerLoadFirstImage,
    offerUrl,
    iOS,
    shouldShowSaleNumbers,
    windowSearch,
    offerMetaData,
    offerLinkIncludesFilters,
  } = props

  const customerType = useMemo(() => getCustomerType(windowSearch), [windowSearch])

  const [count, loading] = useHotelOfferSales(offer?.id, customerType)
  const { locationHeading, locationSubheading, badge, images: offerImages } = offer
  const location = [locationHeading, locationSubheading].filter(t => t).join(', ')

  const checkInDate = filters?.checkIn ? moment(filters.checkIn) : undefined
  const cancellationPolicyType = bestPricePackage?.roomRate?.cancellationPolicy?.type

  const showBadge = !!badge && config.BRAND === 'luxuryescapes'

  // This is temporary until we can improve memory on the offer listing page
  const images = iOS ? take(offerImages, 1, 0) : offerImages

  return (
    <Main>
      <MainImageSegment>
        <ImageCarousel
          images={images}
          eagerLoadFirstImage={eagerLoadFirstImage}
        />
        <OfferOverlayContainer>
          <OfferPartnershipLabel offer={offer} variant="icon" />
        </OfferOverlayContainer>
        {showBadge && badge && <StyledOfferBadge badge={badge} dimensions={{ width: 86, height: 86 }} />}
      </MainImageSegment>
      <div>
        { shouldShowSaleNumbers && !loading && <PeopleLikeMeSalesBanner customerType={customerType} saleCount={count} />}
        <DetailSegment>
          <TripGuard>
            <StyledCSSBreakpoint min="sm">
              <FloatingBookmarkContainer>
                <BookmarkButton offer={offer} />
              </FloatingBookmarkContainer>
            </StyledCSSBreakpoint>
            <StyledCSSBreakpoint max="xs">
              <OffsetBookmarkContainer>
                <BookmarkButton offer={offer} />
              </OffsetBookmarkContainer>
            </StyledCSSBreakpoint>
          </TripGuard>
          <SearchTilePropertyDetails
            location={location}
            cancellationPolicyType={cancellationPolicyType}
            offer={offer}
            checkInDate={checkInDate}
            filters={filters}
            offerUrl={offerUrl}
           />
          <SearchTilePriceDetails
            offer={offer}
            soldOut={soldOut}
            offerUrl={offerUrl}
            bestPriceForDates={bestPriceForDates}
            filters={filters}
            checkInDate={checkInDate}
            bestPricePackage={bestPricePackage}
          />
          {config.ENABLE_BUNDLE_AND_SAVE && offerMetaData?.bundleOfferId && (
            <ViewBundleOfferWrapper>
              <ViewBundleOffer offer={offer} filters={filters} offerMetaData={offerMetaData} offerLinkIncludesFilters={offerLinkIncludesFilters} />
            </ViewBundleOfferWrapper>
          )}
        </DetailSegment>
      </div>
    </Main>
  )
}

const mapStateToProps = (state: App.State): MappedProps => ({
  iOS: state.config.userAgent.osName === 'iOS',
  shouldShowSaleNumbers: state.router.location.pathname.endsWith('people-like-me'),
  windowSearch: state.router.location.search,
})

export default connect(mapStateToProps)(NewSearchFlashOfferExpanded)
